import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { SupportService } from '../support.service';

@Component({
    selector: 'app-support-ticket-detail',
    standalone: false,
    templateUrl: './support-ticket-detail.component.html',
    styleUrls: ['./support-ticket-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportTicketDetailComponent implements OnChanges {
    @Input() issueKey: string | null = null;
    @Output() confirmed = new EventEmitter<void>();

    ticket: any = null;
    comments: any[] = [];
    loading = true;
    error: string | null = null;
    newComment = '';
    submittingComment = false;

    // Confirm completion
    confirmingPrompt = false;
    confirming = false;
    confirmError: string | null = null;

    constructor(
        private supportService: SupportService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnChanges(): void {
        if (this.issueKey) {
            this.confirmingPrompt = false;
            this.confirming = false;
            this.confirmError = null;
            this.loadTicket();
        }
    }

    loadTicket(): void {
        if (!this.issueKey) return;

        this.loading = true;
        this.error = null;

        this.supportService.getTicket(this.issueKey).subscribe({
            next: (data) => {
                this.ticket = data;
                this.comments = data.comments?.values ?? [];
                this.loading = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.error = 'Unable to load ticket details.';
                this.loading = false;
                this.cdr.markForCheck();
            },
        });
    }

    addComment(): void {
        if (!this.issueKey || !this.newComment.trim() || this.submittingComment) return;

        this.submittingComment = true;
        this.supportService.addComment(this.issueKey, this.newComment.trim()).subscribe({
            next: () => {
                this.newComment = '';
                this.submittingComment = false;
                this.loadTicket();
            },
            error: () => {
                this.submittingComment = false;
                this.cdr.markForCheck();
            },
        });
    }

    get isDone(): boolean {
        return (this.ticket?.currentStatus?.statusCategory || '').toLowerCase() === 'done';
    }

    startConfirm(): void {
        this.confirmError = null;
        this.confirmingPrompt = true;
    }

    cancelConfirm(): void {
        this.confirmingPrompt = false;
    }

    confirm(): void {
        if (!this.issueKey || this.confirming) return;

        this.confirming = true;
        this.confirmError = null;
        this.supportService.confirmTicket(this.issueKey).subscribe({
            next: () => {
                this.confirming = false;
                this.confirmingPrompt = false;
                this.confirmed.emit();
            },
            error: () => {
                this.confirming = false;
                this.confirmError = 'Could not confirm the ticket. Please try again.';
                this.cdr.markForCheck();
            },
        });
    }

    getFieldValue(fieldId: string): string {
        const fields = this.ticket?.requestFieldValues;
        if (Array.isArray(fields)) {
            const field = fields.find((f: any) => f.fieldId === fieldId);
            return field?.value ?? '';
        }
        return fields?.[fieldId] ?? '';
    }
}
